import { useEffect } from "react";
import { getRecords } from "../../api/useRecords";
import { getStore } from "../../store/store";
import { AssistantItem } from "../../store/store.types";
import { activeServicesProvidedFilter, SERVICES_PROVIDED_ENTITY } from "../../screens/treatments/treatment-prescription/utils";
import { useUser } from "../../api/useAuth";
import { ServiceProvided, Treatment } from "../../screens/treatments/treatment-prescription/types";
import { formatClientName, isInternalStaff } from "./utils";
import { ROLES } from "../../utils/constants";

const BIWEEKLY_QUANTITY = 2;

const TREATMENT_FIELDS = [
    "id",
    "client.client_id",
    "client.user_id.first_name",
    "client.user_id.last_name",
    "treatment_prescription.services_provided.*",
    "treatment_prescription.services_provided.service.name",
    "treatment_prescription.services_provided.reserved_slots.id",
].join(",");

type TreatmentWithPrescription = Treatment & {
    treatment_prescription: Array<{
        id: number;
        services_provided: Array<ServiceProvided>;
    }>;
};

const skipWarningsWhenReadIsNotPermitted = () => undefined;

const needsReservedSlots = (serviceProvided: ServiceProvided, reservedSlotsByServiceProvided: Record<number, boolean>) => {
    const isWeekly = serviceProvided.session_periodicity === "weekly";
    const isBiweekly = serviceProvided.session_periodicity === "monthly" && serviceProvided.session_quantity === BIWEEKLY_QUANTITY;
    const hasNoReservedSlots = !reservedSlotsByServiceProvided[serviceProvided.id];
    
    return (isWeekly || isBiweekly) && hasNoReservedSlots;
};

export const useReservedSlotsCheck = () => {
    const { data: currentUser } = useUser();

    useEffect(() => {
        if (!currentUser?.id) return;

        const isTechnicalDirector = currentUser?.role?.id === ROLES.technical_director;
        const activeTreatmentsFilter = { treatment_status_new: { _eq: "all_good" } };
        const treatmentsFilter = isTechnicalDirector
            ? activeTreatmentsFilter
            : { _and: [activeTreatmentsFilter, { client_owner: { user_id: { _eq: "$CURRENT_USER" } } }] };

        const checkReservedSlots = async () => {
            const { items: treatments } = await getRecords(
                "treatments",
                TREATMENT_FIELDS,
                {
                    extraOptions: {
                        filter: treatmentsFilter,
                        pageSize: -1,
                    },
                }
            );

            if (treatments.length === 0) return;

            // Collect all service provided IDs to fetch reserved slots
            const allServiceProvidedIds: Array<number> = [];
            (treatments as Array<TreatmentWithPrescription>).forEach((treatment) => {
                const servicesProvided = treatment.treatment_prescription?.flatMap(
                    (prescription) => prescription[SERVICES_PROVIDED_ENTITY] || []
                ) || [];
                servicesProvided.forEach((sp) => allServiceProvidedIds.push(sp.id));
            });

            // Fetch reserved slots separately (more reliable than nested query)
            const { items: reservedSlots } = await getRecords(
                "reserved_slots",
                "id,services_provided",
                {
                    extraOptions: {
                        filter: {
                            services_provided: { _in: allServiceProvidedIds },
                        },
                        pageSize: -1,
                    },
                }
            );

            const reservedSlotsByServiceProvided: Record<number, boolean> = {};
            (reservedSlots as Array<{ id: number; services_provided: number }>).forEach((slot) => {
                reservedSlotsByServiceProvided[slot.services_provided] = true;
            });

            const { assistantDialog } = getStore();

            (treatments as Array<TreatmentWithPrescription>).forEach((treatment) => {
                const clientData = treatment.client as any;

                if (isInternalStaff(clientData)) return;

                const clientName = formatClientName(clientData);

                const servicesProvided = treatment.treatment_prescription?.flatMap(
                    (prescription) => prescription[SERVICES_PROVIDED_ENTITY] || []
                ) || [];

                const activeServicesProvided = servicesProvided.filter(activeServicesProvidedFilter);

                activeServicesProvided.forEach((serviceProvided) => {
                    const needsSlots = needsReservedSlots(serviceProvided, reservedSlotsByServiceProvided);

                    if (needsSlots) {
                        const serviceName = serviceProvided.service?.name || "Serviço";
                        const periodicityLabel = serviceProvided.session_periodicity === "weekly" ? "semanal" : "mensal";

                        const assistantItem: AssistantItem = {
                            id: `reserved-slot-${serviceProvided.id}`,
                            type: "reserved_slot",
                            treatmentId: treatment.id,
                            clientName: clientName,
                            content: `${serviceName} (${periodicityLabel}, ${serviceProvided.session_quantity}x) sem horários reservados`,
                            linkPath: `/treatments/${treatment.id}/detail`,
                            createdAt: new Date().toISOString(),
                        };

                        assistantDialog.addItem(assistantItem);
                    }
                });
            });
        };

        checkReservedSlots().catch(skipWarningsWhenReadIsNotPermitted);
    }, [currentUser?.id]);
};

